import React, { useState } from "react";
import { Link } from "react-router-dom";
import { usePartners, Partner } from "@/context/PartnerContext";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/layouts/PageHeader";
import { StatCard } from "@/components/ui/StatCard";
import { EmptyState } from "@/components/ui/EmptyState";
import {
  Check,
  Edit,
  MapPin,
  MoreVertical,
  Plus,
  Search,
  Trash,
  X,
  HeartHandshake,
  Map,
  ArrowRight,
  Mail,
  UserPlus,
  Link as LinkIcon,
  Phone,
  Users,
  TrendingUp,
  Copy
} from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useLeads } from "@/context/LeadContext";
import ServiceBadge from "@/components/ui-custom/ServiceBadge";
import { Loader2 } from "lucide-react";

const Partners: React.FC = () => {
  const { partners, removePartner, refreshPartners } = usePartners();
  const { leads } = useLeads();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [partnerToDelete, setPartnerToDelete] = useState<Partner | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showRegLinkDialog, setShowRegLinkDialog] = useState(false);

  const filteredPartners = partners.filter(
    (partner) =>
      partner.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      partner.phone.includes(searchTerm) ||
      partner.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const activePartners = filteredPartners.filter((partner) => partner.active);
  const inactivePartners = filteredPartners.filter((partner) => !partner.active);

  const getPartnerLeadCount = (partnerId: string) => {
    return leads.filter((lead) => lead.partnerId === partnerId).length;
  };

  const getPartnerSuccessCount = (partnerId: string) => {
    return leads.filter(
      (lead) => lead.partnerId === partnerId && lead.stage === "έκλεισεΗΔουλειά"
    ).length;
  };

  const handleAddSuccess = () => {
    setShowAddDialog(false);
    refreshPartners();
    toast({
      title: "Επιτυχής προσθήκη!",
      description: "Ο συνεργάτης προστέθηκε επιτυχώς.",
    });
  };

  const handleDeletePartner = async () => {
    if (!partnerToDelete) return;

    setIsDeleting(true);
    try {
      await removePartner(partnerToDelete.id);
      setPartnerToDelete(null);
      toast({
        title: "Επιτυχής διαγραφή!",
        description: "Ο συνεργάτης διαγράφηκε επιτυχώς.",
      });
    } catch (error) {
      console.error("Error deleting partner:", error);
      toast({
        title: "Σφάλμα",
        description: error instanceof Error ? error.message : "Υπήρξε πρόβλημα κατά τη διαγραφή",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const copyRegistrationLink = () => {
    const registrationURL = `${window.location.origin}/partner/register`;
    navigator.clipboard.writeText(registrationURL);
    toast({
      title: "Αντιγράφηκε!",
      description: "Ο σύνδεσμος εγγραφής αντιγράφηκε στο πρόχειρο."
    });
  };

  // Stats at a glance
  const totalLeads = leads.length;
  const totalSuccess = leads.filter(l => l.stage === "έκλεισεΗΔουλειά").length;

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Page Header */}
      <PageHeader
        title="Συνεργάτες"
        description="Διαχείριση δικτύου συνεργατών"
        icon={<HeartHandshake />}
        iconGradient="blue"
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => setShowRegLinkDialog(true)}
              className="border-slate-200 hover:bg-slate-50"
              aria-label="Εμφάνιση συνδέσμου εγγραφής"
            >
              <UserPlus className="mr-2 h-4 w-4" /> Σύνδεσμος Εγγραφής
            </Button>
            <Button asChild variant="outline" className="border-slate-200 hover:bg-slate-50">
              <Link to="/admin/partners/map">
                <Map className="mr-2 h-4 w-4" /> Χάρτης
              </Link>
            </Button>
            <Button asChild variant="gradient">
              <Link to="/admin/partners/new">
                <Plus className="mr-2 h-4 w-4" /> Νέος Συνεργάτης
              </Link>
            </Button>
          </>
        }
      />

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          value={partners.length}
          title="Συνολικά"
          icon={<Users />}
          variant="outlined"
          size="compact"
        />
        <StatCard
          value={activePartners.length}
          title="Ενεργοί"
          icon={<Check />}
          variant="outlined"
          size="compact"
        />
        <StatCard
          value={totalLeads}
          title="Σύν. Leads"
          icon={<HeartHandshake />}
          variant="outlined"
          size="compact"
        />
        <StatCard
          value={totalSuccess}
          title="Επιτυχίες"
          icon={<TrendingUp />}
          variant="outlined"
          size="compact"
        />
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Αναζήτηση συνεργατών..."
          className="pl-10 bg-white border-slate-200 focus:border-blue-500"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <Button
            variant="ghost"
            size="sm"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
            onClick={() => setSearchTerm("")}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="active" className="w-full">
        <TabsList className="bg-slate-100 p-1 rounded-lg">
          <TabsTrigger value="active" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">
            Ενεργοί ({activePartners.length})
          </TabsTrigger>
          <TabsTrigger value="inactive" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">
            Ανενεργοί ({inactivePartners.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="mt-6">
          {activePartners.length === 0 ? (
            <EmptyState
              icon={<Users />}
              title="Δεν βρέθηκαν ενεργοί συνεργάτες"
              description={searchTerm ? "Δοκιμάστε διαφορετικούς όρους αναζήτησης" : undefined}
              action={
                searchTerm ? (
                  <Button variant="ghost" onClick={() => setSearchTerm("")}>
                    Καθαρισμός φίλτρων
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {activePartners.map((partner, index) => (
                <Card
                  key={partner.id}
                  className="group border-slate-200 hover:border-blue-200 hover:shadow-lg transition-all duration-300"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm">
                          {partner.name.charAt(0)}
                        </div>
                        <div>
                          <CardTitle className="text-lg">{partner.name}</CardTitle>
                          <CardDescription className="flex items-center gap-1 mt-0.5">
                            <MapPin className="h-3 w-3" />
                            {partner.location}
                          </CardDescription>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button 
                            variant="ghost" 
                            className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                            aria-label={`Ενέργειες για ${partner.name}`}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Ενέργειες</DropdownMenuLabel>
                          <DropdownMenuItem asChild>
                            <Link to={`/admin/partners/edit/${partner.id}`} className="cursor-pointer flex items-center">
                              <Edit className="mr-2 h-4 w-4" /> Επεξεργασία
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => setPartnerToDelete(partner)}
                            className="text-destructive focus:bg-destructive/10"
                          >
                            <Trash className="mr-2 h-4 w-4" /> Διαγραφή
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    {/* Contact info */}
                    <div className="flex flex-col gap-2 text-sm">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Phone className="h-4 w-4 text-slate-400" />
                        {partner.phone}
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Mail className="h-4 w-4 text-slate-400" />
                        {partner.email || <span className="text-slate-400">-</span>}
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex gap-2">
                      <Badge variant="secondary" className="bg-blue-50 text-blue-700 hover:bg-blue-100">
                        {getPartnerLeadCount(partner.id)} Leads
                      </Badge>
                      <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 hover:bg-emerald-100">
                        {getPartnerSuccessCount(partner.id)} Επιτυχίες
                      </Badge>
                    </div>

                    {/* Services */}
                    <div className="flex flex-wrap gap-1.5">
                      {partner.services && partner.services.length > 0 ? (
                        partner.services.slice(0, 3).map((service, idx) => (
                          <ServiceBadge key={idx} service={service} />
                        ))
                      ) : (
                        <span className="text-xs text-slate-400">Χωρίς υπηρεσίες</span>
                      )}
                      {partner.services && partner.services.length > 3 && (
                        <Badge variant="outline" className="text-xs">+{partner.services.length - 3}</Badge>
                      )}
                    </div>
                  </CardContent>

                  <CardFooter className="pt-0">
                    <Link to={`/admin/partners/${partner.id}/dashboard`} className="w-full">
                      <Button variant="ghost" className="w-full justify-between text-blue-600 hover:text-blue-700 hover:bg-blue-50">
                        Προβολή στατιστικών
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </Link>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="inactive" className="mt-6">
          {inactivePartners.length === 0 ? (
            <div className="text-center py-16 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
              <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 font-medium">Δεν υπάρχουν ανενεργοί συνεργάτες</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {inactivePartners.map((partner) => (
                <Card key={partner.id} className="border-slate-200 opacity-60 hover:opacity-100 transition-opacity">
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-300 flex items-center justify-center text-white font-bold text-sm">
                          {partner.name.charAt(0)}
                        </div>
                        <div>
                          <CardTitle className="text-lg">{partner.name}</CardTitle>
                          <CardDescription className="flex items-center gap-1 mt-0.5">
                            <MapPin className="h-3 w-3" />
                            {partner.location}
                          </CardDescription>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link to={`/admin/partners/edit/${partner.id}`}>
                              <Edit className="mr-2 h-4 w-4" /> Επεξεργασία
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => setPartnerToDelete(partner)} className="text-destructive">
                            <Trash className="mr-2 h-4 w-4" /> Διαγραφή
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Badge variant="outline" className="text-slate-500">Ανενεργός</Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Delete Dialog */}
      <AlertDialog
        open={!!partnerToDelete}
        onOpenChange={(open) => !open && setPartnerToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Είστε βέβαιοι;</AlertDialogTitle>
            <AlertDialogDescription>
              Η ενέργεια αυτή θα διαγράψει μόνιμα τον συνεργάτη
              {partnerToDelete?.name && ` "${partnerToDelete.name}"`}.
              <br /><br />
              <span className="text-amber-600 font-medium">
                Συνεργάτες με leads δεν μπορούν να διαγραφούν.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Άκυρο</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeletePartner}
              className="bg-destructive hover:bg-destructive/90"
              disabled={isDeleting}
            >
              {isDeleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Διαγραφή
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Registration Link Dialog */}
      <Dialog open={showRegLinkDialog} onOpenChange={setShowRegLinkDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Σύνδεσμος Εγγραφής Συνεργατών</DialogTitle>
            <DialogDescription>
              Μοιραστείτε τον σύνδεσμο για αυτόματη εγγραφή συνεργατών.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>Κωδικός εγγραφής:</strong> !Partner25
              </p>
            </div>
            <div className="flex gap-2">
              <Input
                readOnly
                value={`${window.location.origin}/partner/register`}
                className="bg-slate-50"
              />
              <Button onClick={copyRegistrationLink} className="shrink-0">
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Κλείσιμο</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Partners;
